import { useState } from 'react';
import {
  BPM_MAX,
  BPM_MIN,
  GENERATOR_DURATION_MAX,
  GENERATOR_DURATION_MIN,
  MoodSchema,
  type Genre,
  type GeneratorParams,
  type Mood,
} from '../../domain/schemas';
import { describeComposition } from '../../music/generator/apply';
import { compose } from '../../music/generator/composer';
import { GENRES, GENRE_IDS } from '../../music/generator/genres';
import { randomSeed } from '../../music/random';
import { useProject } from '../../state/projectContext';
import { useCommandRunner } from '../../state/useCommandRunner';
import { Alert, Button, Panel } from '../ui';
import { KeySelect } from './TransportBar';

const noNavigate = () => undefined;

/** Genre presets + full, editable composition parameters (all reproducible via the seed). */
export function GeneratorPanel() {
  const { project } = useProject();
  const run = useCommandRunner(noNavigate);
  const last = project.generator;
  const [params, setParams] = useState<GeneratorParams>(() => {
    if (last) return last;
    const g = GENRES['brazilian-phonk'];
    return { genre: 'brazilian-phonk', bpm: g.bpm, key: g.key, mood: g.mood, intensity: 0.7, complexity: 0.5, durationSec: 60, seed: randomSeed(), focus: {} };
  });
  const [state, setState] = useState<{ status: 'idle' | 'working' | 'done' | 'error'; message?: string }>({ status: 'idle' });
  const set = (patch: Partial<GeneratorParams>) => setParams((p) => ({ ...p, ...patch }));

  const choose = (genre: Genre) => {
    const g = GENRES[genre];
    setParams((p) => ({ ...p, genre, bpm: g.bpm, key: g.key, mood: g.mood }));
  };

  const generate = async () => {
    setState({ status: 'working' });
    // Yield a frame so the "Composing…" state is announced before the (fast, synchronous) work.
    await new Promise((r) => setTimeout(r, 30));
    const result = await run([{ type: 'music.generate', spec: params }]);
    if (result.status === 'success') setState({ status: 'done', message: result.applied[0] });
    else setState({ status: 'error', message: result.failure?.message ?? 'Generation failed.' });
  };

  const preview = (() => {
    try {
      const c = compose(params);
      return `${c.sections.length} sections · ${c.bars} bars ≈ ${Math.round(c.durationSec)} s`;
    } catch {
      return '';
    }
  })();

  return (
    <Panel title="Genre presets & procedural composer">
      <p className="mb-3 text-xs text-zinc-400">
        Deterministic, rule-based composition in your browser — not AI. The same settings and seed always produce the same song. Generating replaces the
        patterns, arrangement and mixer of this project.
      </p>
      <div role="radiogroup" aria-label="Genre" className="mb-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        {GENRE_IDS.map((g) => (
          <label
            key={g}
            className={`cursor-pointer rounded-lg border p-2.5 text-xs has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent-400 ${
              params.genre === g ? 'border-accent-400 bg-accent-600/15' : 'border-zinc-800 bg-zinc-950/60 hover:border-zinc-600'
            }`}
          >
            <input type="radio" name="genre" value={g} checked={params.genre === g} onChange={() => choose(g)} className="sr-only" data-testid={`genre-${g}`} />
            <span className="block text-sm font-semibold text-zinc-100">{GENRES[g].label}</span>
            <span className="mt-1 block text-zinc-400">{GENRES[g].description}</span>
            <span className="mt-1 block text-zinc-400">
              ~{GENRES[g].bpm} BPM
            </span>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="gen-bpm" className="text-xs text-zinc-300">
            BPM
          </label>
          <input
            id="gen-bpm"
            type="number"
            min={BPM_MIN}
            max={BPM_MAX}
            value={params.bpm}
            onChange={(e) => set({ bpm: Math.round(Math.min(BPM_MAX, Math.max(BPM_MIN, Number(e.target.value) || BPM_MIN))) })}
            className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm"
          />
        </div>
        <KeySelect idPrefix="gen-key" value={params.key} onChange={(key) => set({ key })} />
        <div className="flex flex-col gap-1">
          <label htmlFor="gen-mood" className="text-xs text-zinc-300">
            Mood
          </label>
          <select id="gen-mood" value={params.mood} onChange={(e) => set({ mood: e.target.value as Mood })} className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm">
            {MoodSchema.options.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="gen-duration" className="text-xs text-zinc-300">
            Duration (seconds)
          </label>
          <input
            id="gen-duration"
            type="number"
            min={GENERATOR_DURATION_MIN}
            max={GENERATOR_DURATION_MAX}
            value={params.durationSec}
            onChange={(e) =>
              set({ durationSec: Math.round(Math.min(GENERATOR_DURATION_MAX, Math.max(GENERATOR_DURATION_MIN, Number(e.target.value) || GENERATOR_DURATION_MIN))) })
            }
            className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="gen-intensity" className="text-xs text-zinc-300">
            Intensity {Math.round(params.intensity * 100)}%
          </label>
          <input id="gen-intensity" type="range" min={0} max={100} value={Math.round(params.intensity * 100)} onChange={(e) => set({ intensity: Number(e.target.value) / 100 })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="gen-complexity" className="text-xs text-zinc-300">
            Complexity {Math.round(params.complexity * 100)}%
          </label>
          <input id="gen-complexity" type="range" min={0} max={100} value={Math.round(params.complexity * 100)} onChange={(e) => set({ complexity: Number(e.target.value) / 100 })} />
        </div>
        <div className="col-span-2 flex flex-col gap-1">
          <label htmlFor="gen-seed" className="text-xs text-zinc-300">
            Seed
          </label>
          <div className="flex gap-2">
            <input
              id="gen-seed"
              type="number"
              min={0}
              max={2147483647}
              value={params.seed}
              onChange={(e) => set({ seed: Math.max(0, Math.min(2147483647, Math.floor(Number(e.target.value) || 0))) })}
              className="w-36 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 font-mono text-sm"
              data-testid="gen-seed"
            />
            <Button size="sm" onClick={() => set({ seed: randomSeed() })}>
              New seed
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={() => void generate()} disabled={state.status === 'working'} data-testid="generate">
          {state.status === 'working' ? 'Composing…' : 'Generate song'}
        </Button>
        <span className="text-xs text-zinc-400">{preview}</span>
      </div>
      <div aria-live="polite" className="mt-3">
        {state.status === 'working' && <p className="text-xs text-zinc-400">Composing…</p>}
        {state.status === 'done' && (
          <Alert tone="success" title="Song generated">
            <span data-testid="generate-result">{state.message}</span>
          </Alert>
        )}
        {state.status === 'error' && <Alert title="Could not generate">{state.message}</Alert>}
        {state.status === 'idle' && last && (
          <p className="text-xs text-zinc-400" data-testid="last-generation">
            Current song: {describeComposition(compose(last))}
          </p>
        )}
      </div>
    </Panel>
  );
}
